import type { HttpLayer } from '../http.js';
import type {
  AddSupportCommentRequest,
  AddSupportCommentResponse,
  AttachSupportArtifactRequest,
  CreateSupportTicketRequest,
  GetSupportTicketParams,
  ListSupportTicketsParams,
  ListSupportTicketsResponse,
  SupportAttachment,
  SupportTicket,
  SupportTicketDetail,
  SupportUnseenCount,
  UpdateSupportTicketRequest,
} from '../types/support.js';

export function encodeSupportCursor(next: { last_activity_at: string; id: string }): string {
  return `${next.last_activity_at}|${next.id}`;
}

export class SupportResource {
  constructor(private http: HttpLayer) {}

  async create(params: CreateSupportTicketRequest): Promise<SupportTicket> {
    return this.http.post('/v1/support/tickets', params);
  }

  async list(params: ListSupportTicketsParams = {}): Promise<ListSupportTicketsResponse> {
    return this.http.get('/v1/support/tickets', params);
  }

  async get(ticketId: string, params: GetSupportTicketParams = {}): Promise<SupportTicketDetail> {
    return this.http.get(`/v1/support/tickets/${ticketId}`, params);
  }

  async comment(
    ticketId: string,
    params: AddSupportCommentRequest,
  ): Promise<AddSupportCommentResponse> {
    return this.http.post(`/v1/support/tickets/${ticketId}/comments`, params);
  }

  async attach(
    ticketId: string,
    params: AttachSupportArtifactRequest,
  ): Promise<SupportAttachment> {
    return this.http.post(`/v1/support/tickets/${ticketId}/attachments`, params);
  }

  async markRead(ticketId: string): Promise<SupportUnseenCount> {
    return this.http.post(`/v1/support/tickets/${ticketId}/read`, {});
  }

  async unseen(): Promise<SupportUnseenCount> {
    return this.http.get('/v1/support/unseen');
  }

  async content(ticketId: string, attachmentId: string): Promise<ArrayBuffer> {
    return this.http.get(
      `/v1/support/tickets/${ticketId}/attachments/${attachmentId}/content`,
      undefined,
      { acceptBinary: true },
    );
  }

  async adminList(params: ListSupportTicketsParams = {}): Promise<ListSupportTicketsResponse> {
    return this.http.get('/v1/support/admin/tickets', params);
  }

  async adminGet(ticketId: string): Promise<SupportTicketDetail> {
    return this.http.get(`/v1/support/admin/tickets/${ticketId}`);
  }

  async adminUpdate(
    ticketId: string,
    params: UpdateSupportTicketRequest,
  ): Promise<SupportTicket> {
    return this.http.patch(`/v1/support/admin/tickets/${ticketId}`, params);
  }

  async adminComment(
    ticketId: string,
    params: AddSupportCommentRequest,
  ): Promise<AddSupportCommentResponse> {
    return this.http.post(`/v1/support/admin/tickets/${ticketId}/comments`, params);
  }

  async adminAttach(
    ticketId: string,
    params: AttachSupportArtifactRequest,
  ): Promise<SupportAttachment> {
    return this.http.post(`/v1/support/admin/tickets/${ticketId}/attachments`, params);
  }

  async adminMarkRead(ticketId: string): Promise<SupportUnseenCount> {
    return this.http.post(`/v1/support/admin/tickets/${ticketId}/read`, {});
  }
}
